import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  Link as MuiLink,
  Avatar,
  Drawer,
  CircularProgress,
  SelectChangeEvent,
  InputLabel
} from '@mui/material';
import {
  Search,
  MoreVert,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew,
  Close as CloseIcon,
  UnfoldMore,
  ContentCopy
} from '@mui/icons-material';
import {
  KadryEmployee,
  EmployeeKind,
  listEmployees,
  getEmployee,
  updateEmployee,
  createExternalEmployee,
  listEmployeeLeaveRequests,
  listEmployeeDocuments,
  createEmployeeDocument,
  deleteEmployeeDocument,
  KadryLeaveRequest,
  KadryDocument,
  ExternalEmployeeCreatePayload,
  KadryEmployeeUpdatePayload
} from '../services/kadryService';

// -----------------------------------------------------------------------------
// Style globalne (odwzorowane 1:1 z ProjectsPage.tsx)
// -----------------------------------------------------------------------------

const PRIMARY_BLUE = '#4040DB';
const PRIMARY_BLUE_HOVER = '#1F1FB0';
const PRIMARY_BLUE_ACTIVE = '#1A1A98';
const HEADER_TEXT = '#020242';
const TEXT_DARK = '#2D2D32';
const TEXT_MUTED = '#595966';
const BORDER_LIGHT = '#d4d4d9';
const BORDER_TABLE = '#d0d0d6';
const BG_SOFT = '#FAFAFA';
const ROW_HOVER = '#F5F5FF';
const TAB_BG = '#F1F1F6';
const TAB_BG_ACTIVE = '#E6E6EC';
const PLACEHOLDER = '#B9B9B9';

const filterLabelSx = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '16px',
  color: TEXT_DARK
} as const;

const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 36,
    backgroundColor: '#fff',
    borderRadius: '4px'
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER_LIGHT },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TEXT_MUTED },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: `${TEXT_MUTED} !important` },
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
  '& .MuiInputBase-input': {
    px: '12px',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
    color: TEXT_DARK
  },
  '& .MuiInputBase-input::placeholder': { color: PLACEHOLDER, opacity: 1 }
} as const;

const sortLabelBaseSx = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  color: TEXT_DARK,
  display: 'inline-flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: '4px',
  '& .MuiTableSortLabel-icon': {
    opacity: 1,
    width: 12,
    height: 12,
    ml: '4px'
  }
} as const;
const StrongSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: TEXT_DARK }} />);
const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: BORDER_LIGHT }} />);
const getSortLabelProps = (isActive: boolean) => ({
  IconComponent: (isActive ? StrongSortIcon : MutedSortIcon) as any,
  sx: {
    ...sortLabelBaseSx,
    '& .MuiTableSortLabel-icon': {
      ...(sortLabelBaseSx as any)['& .MuiTableSortLabel-icon'],
      color: isActive ? TEXT_DARK : BORDER_LIGHT
    }
  }
}) as const;

const tabSx = {
  textTransform: 'none',
  color: HEADER_TEXT,
  fontFamily: 'Roboto, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  px: 3,
  minHeight: 32,
  height: 32,
  mt: '8px',
  bgcolor: TAB_BG,
  borderRadius: '4px 4px 0 0',
  '&.Mui-selected': { mt: 0, height: 40, bgcolor: TAB_BG_ACTIVE, color: HEADER_TEXT, fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
  '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: HEADER_TEXT }
} as const;

const tableSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': { padding: '6px 10px', fontSize: '0.85rem', borderRadius: 0 },
  '& .MuiTableCell-head': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: BG_SOFT,
    borderRight: `1px solid ${BORDER_LIGHT}`,
    borderRadius: 0,
    boxShadow: 'none'
  },
  '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0 },
  '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
} as const;

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

type SortField =
  | 'full_name'
  | 'contract_type'
  | 'pesel'
  | 'position'
  | 'email'
  | 'medical_exam_valid_until'
  | 'leave_days_used'
  | 'contract_end_date';
type SortOrder = 'asc' | 'desc';
type TabKey = 'all' | 'b2b' | 'field' | 'office';

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    const iso = d.length === 10 ? d + 'T12:00:00Z' : d;
    return new Intl.DateTimeFormat('pl-PL').format(new Date(iso));
  } catch {
    return String(d);
  }
}

function daysUntil(d: string | null | undefined): number | null {
  if (!d) return null;
  try {
    const iso = d.length === 10 ? d + 'T12:00:00Z' : d;
    const diff = Math.floor((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff;
  } catch {
    return null;
  }
}

function medicalStatus(valid_until: string | null | undefined): { label: string; color: 'success' | 'warning' | 'error' | 'default' } {
  if (!valid_until) return { label: 'brak danych', color: 'default' };
  const du = daysUntil(valid_until);
  if (du == null) return { label: 'brak danych', color: 'default' };
  if (du < 0) return { label: 'nieaktualne', color: 'error' };
  if (du <= 30) return { label: `za ${du} dni`, color: 'warning' };
  return { label: 'aktualne', color: 'success' };
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function numberOrZero(v: number | null | undefined): number {
  if (v == null) return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return 0;
}

function contractTypeLabel(ct: string | null | undefined): string {
  if (!ct) return '—';
  const m: Record<string, string> = { uop: 'UoP', b2b: 'B2B', uz: 'Umowa zlecenie' };
  return m[String(ct).toLowerCase()] || String(ct);
}

/** Wiersz tabeli: status bad. lek. + ewent. „N dni” (tylko gdy jeszcze ważne). */
function medicalExamTableRow(validUntil: string | null | undefined): { line1: string; line2: string | null; tone: 'ok' | 'bad' | 'muted' } {
  if (!validUntil) return { line1: 'brak danych', line2: null, tone: 'muted' };
  const du = daysUntil(validUntil);
  if (du == null) return { line1: 'brak danych', line2: null, tone: 'muted' };
  if (du < 0) return { line1: 'Nieaktualne', line2: null, tone: 'bad' };
  return { line1: 'Aktualne', line2: `${du} dni`, tone: 'ok' };
}

function umowaDoText(r: KadryEmployee): string {
  if (r.contract_is_fixed_term === false) return 'Nieokreślony';
  if (r.contract_is_fixed_term === true) {
    if (r.contract_end_date) {
      const d = String(r.contract_end_date);
      return d.length >= 10 ? d.slice(0, 10) : d;
    }
    return '—';
  }
  if (r.contract_end_date) {
    const d = String(r.contract_end_date);
    return d.length >= 10 ? d.slice(0, 10) : d;
  }
  return '—';
}

function leaveFractionText(r: KadryEmployee): string {
  if (r.leave_days_used == null && r.leave_days_entitlement == null) return '—';
  const u = numberOrZero(r.leave_days_used);
  const e = r.leave_days_entitlement;
  if (e == null) return `${u}/—`;
  return `${u}/${e}`;
}

function CopyValueBtn({ value, ariaLabel }: { value: string | null | undefined; ariaLabel: string }) {
  if (!value?.trim()) return null;
  return (
    <Tooltip title="Kopiuj">
      <IconButton
        size="small"
        aria-label={ariaLabel}
        onClick={(e) => { e.stopPropagation(); void navigator.clipboard.writeText(value); }}
        sx={{ p: 0.25, ml: 0.5, verticalAlign: 'middle' }}
      >
        <ContentCopy sx={{ fontSize: 16, color: TEXT_MUTED }} />
      </IconButton>
    </Tooltip>
  );
}

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

const KadryPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<KadryEmployee[]>([]);
  const [loading, setLoading] = React.useState(false);

  const [searchText, setSearchText] = React.useState('');
  const [positionFilter, setPositionFilter] = React.useState<string>('');

  // Zakładki (analogicznie do Projektów: "W realizacji / Zakończone…")
  const [tabValue, setTabValue] = React.useState<number>(0);

  // Sortowanie
  const [sortField, setSortField] = React.useState<SortField>('full_name');
  const [sortOrder, setSortOrder] = React.useState<SortOrder>('asc');

  // Menu kontekstowe wiersza
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = React.useState<KadryEmployee | null>(null);

  // Dialogi
  const [addOpen, setAddOpen] = React.useState(false);
  const [detailsEmployee, setDetailsEmployee] = React.useState<KadryEmployee | null>(null);
  const [detailsTab, setDetailsTab] = React.useState(0);

  // Wysokość tabeli — naśladujemy Projekty: 432 domyślnie albo pełna dostępna
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [tableHeight, setTableHeight] = React.useState<number | null>(null);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const list = await listEmployees();
      setRows(list);
    } catch (e) {
      console.error('[KadryPage] load failed', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Auto-wysokość tabeli: dopasuj do dostępnego miejsca (jak w Projektach)
  React.useEffect(() => {
    const recompute = () => {
      try {
        const el = tableContainerRef.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        const avail = Math.max(320, Math.floor((window.innerHeight || 0) - top - 24));
        setTableHeight(avail);
      } catch {}
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [rows.length]);

  // Lista stanowisk do filtra (unikalne, niepuste)
  const positionOptions = React.useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.position && r.position.trim()) set.add(r.position.trim()); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pl'));
  }, [rows]);

  const baseFiltered = React.useMemo(() => {
    const ql = searchText.trim().toLowerCase();

    return rows.filter(r => {
      if (ql) {
        const hay = [r.full_name, r.email || '', r.phone || '', r.position || '', r.kadry_notes || '', r.pesel || ''].join(' ').toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      if (positionFilter && (r.position || '') !== positionFilter) return false;
      return true;
    });
  }, [rows, searchText, positionFilter]);

  const tabCounts = React.useMemo(() => {
    const counts = { all: 0, b2b: 0, field: 0, office: 0 } as Record<TabKey, number>;
    baseFiltered.forEach(r => {
      counts.all += 1;
      if (String(r.contract_type || '').toLowerCase() === 'b2b') counts.b2b += 1;
      if (r.kadry_work_mode === 'field') counts.field += 1;
      if (r.kadry_work_mode === 'office') counts.office += 1;
    });
    return counts;
  }, [baseFiltered]);

  const tabbed = React.useMemo(() => {
    if (tabValue === 1) return baseFiltered.filter(r => String(r.contract_type || '').toLowerCase() === 'b2b');
    if (tabValue === 2) return baseFiltered.filter(r => r.kadry_work_mode === 'field');
    if (tabValue === 3) return baseFiltered.filter(r => r.kadry_work_mode === 'office');
    return baseFiltered;
  }, [baseFiltered, tabValue]);

  const sorted = React.useMemo(() => {
    const dir = sortOrder === 'asc' ? 1 : -1;
    const list = [...tabbed];
    list.sort((a, b) => {
      const getVal = (x: KadryEmployee): string | number | null | undefined => {
        if (sortField === 'pesel') return (x as any).pesel ?? '';
        if (sortField === 'contract_type') return (x as any).contract_type ?? '';
        return (x as any)[sortField];
      };
      const va = getVal(a);
      const vb = getVal(b);
      if (va == null && vb == null) return 0;
      if (va == null || va === '') return 1;
      if (vb == null || vb === '') return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), 'pl') * dir;
    });
    return list;
  }, [tabbed, sortField, sortOrder]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortOrder('asc'); }
  };

  const handleRowClick = (r: KadryEmployee) => {
    // Przejście na oddzielną stronę pracownika (styl jak /projects/:id).
    // Przekazujemy `kind` w query, żeby detal wiedział w którym zbiorze szukać
    // (user vs external) bez dodatkowego zapytania do backendu.
    navigate(`/app/kadry/${encodeURIComponent(String(r.id))}?kind=${r.kind}`);
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>, r: KadryEmployee) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(r);
  };
  const handleCloseMenu = () => { setMenuAnchor(null); setMenuRow(null); };

  return (
    <Box sx={{ bgcolor: BG_SOFT, mt: '20px' }}>
      {/* Nagłówek + przycisk "Dodaj pracownika" (1:1 styl ProjectsPage) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: HEADER_TEXT,
            m: 0,
            mt: '8.5px'
          }}
        >
          Kadry
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Button
            variant="contained"
            onClick={() => setAddOpen(true)}
            data-action="add"
            data-hotkey-add="true"
            sx={{
              width: 160,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              backgroundColor: PRIMARY_BLUE,
              color: '#fff',
              '&:hover': { backgroundColor: PRIMARY_BLUE_HOVER },
              '&:active': { backgroundColor: PRIMARY_BLUE_ACTIVE }
            }}
          >
            Dodaj pracownika
          </Button>
        </Box>
      </Box>

      {/* Rząd filtrów — grid jak w /projects */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '300px 300px',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: BG_SOFT
        }}
      >
        {/* Szukaj */}
        <TextField
          size="small"
          placeholder="Szukaj w kadrach"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          inputProps={{
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); try { (e.currentTarget as HTMLInputElement).blur(); } catch {} }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: TEXT_MUTED }} />
              </InputAdornment>
            )
          }}
          sx={{ width: 300, mt: '20px', ...filterFieldSx }}
        />

        {/* Stanowisko */}
        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={filterLabelSx}>Stanowisko</Typography>
          <Select
            size="small"
            value={positionFilter}
            onChange={(e: SelectChangeEvent<string>) => setPositionFilter(e.target.value)}
            displayEmpty
            renderValue={positionFilter !== '' ? undefined : () => 'Wszystkie'}
            IconComponent={(iconProps: any) => (
              <Box
                component="img"
                alt="dropdown"
                src="/assets/filters/keyboard_arrow_down.png"
                {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{
              height: 36,
              pr: '8px',
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                height: 36,
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                color: positionFilter ? TEXT_DARK : PLACEHOLDER
              },
              backgroundColor: '#fff',
              borderRadius: '4px',
              '& fieldset': { borderColor: BORDER_LIGHT },
              '&:hover fieldset': { borderColor: TEXT_MUTED },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: `${TEXT_MUTED} !important` },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {positionOptions.map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
          </Select>
        </Box>
      </Box>

      {/* Zakładki — styl kart (1:1 z Projektami) */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          aria-label="kadry tabs"
          sx={{
            minHeight: 40,
            height: 40,
            flexGrow: 1,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: '2px' }
          }}
        >
          <Tab disableRipple label={`Wszyscy (${tabCounts.all})`} sx={tabSx} />
          <Tab disableRipple label={`B2B (${tabCounts.b2b})`} sx={tabSx} />
          <Tab disableRipple label={`Teren i produkcja (${tabCounts.field})`} sx={tabSx} />
          <Tab disableRipple label={`Praca biurowa (${tabCounts.office})`} sx={tabSx} />
        </Tabs>
      </Box>

      {/* Tabela — styl 1:1 z Projektami */}
      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        sx={{
          height: tableHeight ?? 432,
          overflow: 'auto',
          position: 'relative',
          border: `1px solid ${BORDER_TABLE}`,
          borderBottom: `1px solid ${BORDER_TABLE}`,
          borderRadius: 0,
          borderTopRightRadius: 4,
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          overflowX: 'hidden',
          overflowY: 'auto',
          boxShadow: 'none',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { width: 0, height: 0 }
        }}
      >
        <Table size="small" stickyHeader sx={tableSx}>
          <colgroup>
            <col style={{ minWidth: 200 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 140 }} />
            <col style={{ minWidth: 220 }} />
            <col style={{ width: 150 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 44 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel active={sortField === 'full_name'} direction={sortField === 'full_name' ? sortOrder : 'asc'} onClick={() => toggleSort('full_name')} {...getSortLabelProps(sortField === 'full_name')}>
                  Imię i nazwisko
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'contract_type'} direction={sortField === 'contract_type' ? sortOrder : 'asc'} onClick={() => toggleSort('contract_type')} {...getSortLabelProps(sortField === 'contract_type')}>
                  Typ umowy
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'pesel'} direction={sortField === 'pesel' ? sortOrder : 'asc'} onClick={() => toggleSort('pesel')} {...getSortLabelProps(sortField === 'pesel')}>
                  PESEL
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'position'} direction={sortField === 'position' ? sortOrder : 'asc'} onClick={() => toggleSort('position')} {...getSortLabelProps(sortField === 'position')}>
                  Stanowisko
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'email'} direction={sortField === 'email' ? sortOrder : 'asc'} onClick={() => toggleSort('email')} {...getSortLabelProps(sortField === 'email')}>
                  E-mail / telefon
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'medical_exam_valid_until'} direction={sortField === 'medical_exam_valid_until' ? sortOrder : 'asc'} onClick={() => toggleSort('medical_exam_valid_until')} {...getSortLabelProps(sortField === 'medical_exam_valid_until')}>
                  Badania lekarskie
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'leave_days_used'} direction={sortField === 'leave_days_used' ? sortOrder : 'asc'} onClick={() => toggleSort('leave_days_used')} {...getSortLabelProps(sortField === 'leave_days_used')}>
                  Urlop
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'contract_end_date'} direction={sortField === 'contract_end_date' ? sortOrder : 'asc'} onClick={() => toggleSort('contract_end_date')} {...getSortLabelProps(sortField === 'contract_end_date')}>
                  Umowa do
                </TableSortLabel>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}

            {!loading && sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3, color: TEXT_MUTED }}>
                  Brak pracowników spełniających kryteria
                </TableCell>
              </TableRow>
            )}

            {!loading && sorted.map((r) => {
              const ex = medicalExamTableRow(r.medical_exam_valid_until);
              const exColor = ex.tone === 'ok' ? '#15803d' : ex.tone === 'bad' ? '#b91c1c' : TEXT_MUTED;
              return (
                <TableRow
                  key={`${r.kind}-${r.id}`}
                  hover
                  onClick={() => handleRowClick(r)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: `${ROW_HOVER} !important` },
                    '&.MuiTableRow-hover:hover': { backgroundColor: `${ROW_HOVER} !important` }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      <Avatar src={r.avatar_url || undefined} sx={{ width: 36, height: 36, fontSize: 14, flexShrink: 0, bgcolor: r.kind === 'user' ? PRIMARY_BLUE : TEXT_MUTED }}>
                        {initialsFromName(r.full_name)}
                      </Avatar>
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, fontWeight: 500, color: HEADER_TEXT, lineHeight: '18px' }}>
                          {r.full_name}
                        </Typography>
                        {!r.is_active && (<Typography variant="caption" sx={{ color: '#B91C1C' }}>Nieaktywny</Typography>)}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: TEXT_DARK, fontFamily: 'Roboto, sans-serif', fontSize: 14 }}>{contractTypeLabel(r.contract_type)}</TableCell>
                  <TableCell sx={{ color: TEXT_DARK, fontFamily: 'Roboto, sans-serif', fontSize: 14, verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                      <span>{r.pesel?.trim() || '—'}</span>
                      {r.pesel?.trim() ? <CopyValueBtn value={r.pesel} ariaLabel="Kopiuj PESEL" /> : null}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: TEXT_DARK, fontFamily: 'Roboto, sans-serif', fontSize: 14 }}>{r.position || '—'}</TableCell>
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: TEXT_DARK }}>{r.email || '—'}</Typography>
                        {r.email?.trim() ? <CopyValueBtn value={r.email} ariaLabel="Kopiuj e-mail" /> : null}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: TEXT_DARK }}>{r.phone?.trim() || '—'}</Typography>
                        {r.phone?.trim() ? <CopyValueBtn value={r.phone} ariaLabel="Kopiuj telefon" /> : null}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, fontWeight: 500, color: exColor }}>{ex.line1}</Typography>
                      {ex.line2 ? (
                        <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13, color: TEXT_DARK }}>{ex.line2}</Typography>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: TEXT_DARK, whiteSpace: 'nowrap' }}>
                    {leaveFractionText(r)}
                  </TableCell>
                  <TableCell sx={{ color: TEXT_DARK, fontFamily: 'Roboto, sans-serif', fontSize: 14 }}>{umowaDoText(r)}</TableCell>
                  <TableCell align="right" sx={{ pr: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={(e) => handleOpenMenu(e, r)} aria-label="Akcje">
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu kontekstowe */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        <MenuItem
          onClick={() => {
            if (menuRow) handleRowClick(menuRow);
            handleCloseMenu();
          }}
        >
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Edytuj
        </MenuItem>
      </Menu>

      {/* Dialog: dodaj pracownika fizycznego */}
      <AddExternalEmployeeDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => { setAddOpen(false); load(); }}
      />

      {/* Drawer: szczegóły pracownika */}
      <EmployeeDetailsDrawer
        employee={detailsEmployee}
        tab={detailsTab}
        onTabChange={setDetailsTab}
        onClose={() => setDetailsEmployee(null)}
        onSaved={() => { load(); }}
      />
    </Box>
  );
};

// -----------------------------------------------------------------------------
// Dialog dodania pracownika fizycznego
// -----------------------------------------------------------------------------

const AddExternalEmployeeDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}> = ({ open, onClose, onCreated }) => {
  const [form, setForm] = React.useState<ExternalEmployeeCreatePayload>({ first_name: '', last_name: '', is_active: true });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) setForm({ first_name: '', last_name: '', is_active: true });
  }, [open]);

  const set = (patch: Partial<ExternalEmployeeCreatePayload>) => setForm(prev => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      window.alert('Podaj imię i nazwisko');
      return;
    }
    try {
      setSaving(true);
      await createExternalEmployee(form);
      onCreated();
    } catch (e: any) {
      console.error('[KadryPage] createExternal failed', e);
      window.alert('Nie udało się dodać pracownika: ' + (e?.response?.data?.message || e?.message || 'błąd'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Roboto, sans-serif', color: HEADER_TEXT, fontWeight: 500 }}>
        Dodaj pracownika fizycznego (bez konta w aplikacji)
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField size="small" label="Imię *" value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} fullWidth />
          <TextField size="small" label="Nazwisko *" value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} fullWidth />
          <TextField size="small" label="Nazwa wyświetlana" value={form.display_name || ''} onChange={(e) => set({ display_name: e.target.value })} fullWidth />
          <TextField size="small" label="Stanowisko" value={form.position || ''} onChange={(e) => set({ position: e.target.value })} fullWidth />
          <TextField size="small" label="Email" value={form.email || ''} onChange={(e) => set({ email: e.target.value })} fullWidth />
          <TextField size="small" label="Telefon" value={form.phone || ''} onChange={(e) => set({ phone: e.target.value })} fullWidth />
          <TextField size="small" label="PESEL" value={form.pesel || ''} onChange={(e) => set({ pesel: e.target.value })} fullWidth />
          <TextField size="small" label="Data zatrudnienia" type="date" InputLabelProps={{ shrink: true }} value={form.hire_date || ''} onChange={(e) => set({ hire_date: e.target.value })} fullWidth />
          <FormControl fullWidth size="small">
            <InputLabel>Typ umowy</InputLabel>
            <Select
              label="Typ umowy"
              value={form.contract_type || ''}
              onChange={(e: SelectChangeEvent) => set({ contract_type: e.target.value || null })}
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="uop">UoP</MenuItem>
              <MenuItem value="b2b">B2B</MenuItem>
              <MenuItem value="uz">Umowa zlecenie</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Charakter umowy</InputLabel>
            <Select
              label="Charakter umowy"
              value={form.contract_is_fixed_term === true ? 'fixed' : form.contract_is_fixed_term === false ? 'indefinite' : ''}
              onChange={(e: SelectChangeEvent) => {
                const v = e.target.value;
                if (v === 'fixed') set({ contract_is_fixed_term: true });
                else if (v === 'indefinite') set({ contract_is_fixed_term: false, contract_end_date: null });
                else set({ contract_is_fixed_term: null, contract_end_date: null });
              }}
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="fixed">Na czas określony</MenuItem>
              <MenuItem value="indefinite">Na czas nieokreślony</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Umowa do (gdy określony czas)"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.contract_end_date || ''}
            onChange={(e) => set({ contract_end_date: e.target.value || null })}
            fullWidth
            disabled={form.contract_is_fixed_term !== true}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Miejsce pracy (widok listy)</InputLabel>
            <Select
              label="Miejsce pracy (widok listy)"
              value={form.kadry_work_mode || ''}
              onChange={(e: SelectChangeEvent) => set({ kadry_work_mode: e.target.value || null })}
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="field">Teren i produkcja</MenuItem>
              <MenuItem value="office">Praca biurowa</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="BHP ogólne — ważne do" type="date" InputLabelProps={{ shrink: true }} value={form.bhp_general_valid_until || ''} onChange={(e) => set({ bhp_general_valid_until: e.target.value || null })} fullWidth />
          <TextField size="small" label="BHP stanowiskowe — ważne do" type="date" InputLabelProps={{ shrink: true }} value={form.bhp_position_valid_until || ''} onChange={(e) => set({ bhp_position_valid_until: e.target.value || null })} fullWidth />
          <TextField size="small" label="Urlop przysługujący (dni)" type="number" value={form.leave_days_entitlement ?? ''} onChange={(e) => set({ leave_days_entitlement: e.target.value === '' ? null : Number(e.target.value) })} fullWidth />
          <TextField size="small" label="Urlop wykorzystany (dni)" type="number" value={form.leave_days_used ?? ''} onChange={(e) => set({ leave_days_used: e.target.value === '' ? null : Number(e.target.value) })} fullWidth />
          <TextField size="small" label="Badania — ostatnie" type="date" InputLabelProps={{ shrink: true }} value={form.medical_exam_last_date || ''} onChange={(e) => set({ medical_exam_last_date: e.target.value })} fullWidth />
          <TextField size="small" label="Badania — ważne do" type="date" InputLabelProps={{ shrink: true }} value={form.medical_exam_valid_until || ''} onChange={(e) => set({ medical_exam_valid_until: e.target.value })} fullWidth />
          <TextField size="small" label="Notatki" multiline minRows={2} value={form.notes || ''} onChange={(e) => set({ notes: e.target.value })} fullWidth sx={{ gridColumn: { md: '1 / span 2' } }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: TEXT_DARK }}>Anuluj</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            textTransform: 'none',
            backgroundColor: PRIMARY_BLUE,
            '&:hover': { backgroundColor: PRIMARY_BLUE_HOVER }
          }}
        >
          {saving ? 'Zapisywanie…' : 'Zapisz'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// -----------------------------------------------------------------------------
// Drawer szczegółów pracownika
// -----------------------------------------------------------------------------

const EmployeeDetailsDrawer: React.FC<{
  employee: KadryEmployee | null;
  tab: number;
  onTabChange: (n: number) => void;
  onClose: () => void;
  onSaved: () => void;
}> = ({ employee, tab, onTabChange, onClose, onSaved }) => {
  const [form, setForm] = React.useState<KadryEmployee | null>(null);
  const [leaves, setLeaves] = React.useState<KadryLeaveRequest[]>([]);
  const [docs, setDocs] = React.useState<KadryDocument[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [docForm, setDocForm] = React.useState<{ title: string; url: string; doc_type: string; notes: string }>({ title: '', url: '', doc_type: '', notes: '' });

  React.useEffect(() => {
    if (!employee) {
      setForm(null);
      return;
    }
    setForm({ ...employee });
    let cancelled = false;
    (async () => {
      try {
        const full = await getEmployee(employee.kind, employee.id);
        if (!cancelled) setForm({ ...full });
      } catch {
        // zostaw dane z listy
      }
    })();
    return () => { cancelled = true; };
  }, [employee]);

  React.useEffect(() => {
    if (!employee) return;
    setLeaves([]);
    setDocs([]);
    setDocForm({ title: '', url: '', doc_type: '', notes: '' });
  }, [employee]);

  React.useEffect(() => {
    if (!employee) return;
    (async () => {
      try {
        const [lv, dc] = await Promise.all([
          listEmployeeLeaveRequests(employee.kind, employee.id).catch(() => []),
          listEmployeeDocuments(employee.kind, employee.id).catch(() => [])
        ]);
        setLeaves(lv);
        setDocs(dc);
      } catch {}
    })();
  }, [employee]);

  if (!employee || !form) {
    return (
      <Drawer anchor="right" open={false} onClose={onClose} PaperProps={{ sx: { width: 560 } }}>
        <Box />
      </Drawer>
    );
  }

  const set = (patch: Partial<KadryEmployee>) => setForm(prev => prev ? { ...prev, ...patch } : prev);

  const handleSave = async () => {
    if (!form) return;
    try {
      setSaving(true);
      const payload: KadryEmployeeUpdatePayload = {
        leave_days_entitlement: form.leave_days_entitlement,
        leave_days_used: form.leave_days_used,
        medical_exam_last_date: form.medical_exam_last_date,
        medical_exam_valid_until: form.medical_exam_valid_until,
        hire_date: form.hire_date,
        termination_date: form.termination_date,
        kadry_notes: form.kadry_notes,
        position: form.position,
        contract_type: form.contract_type ?? null,
        contract_is_fixed_term: form.contract_is_fixed_term ?? null,
        contract_end_date: form.contract_end_date ?? null,
        bhp_general_valid_until: form.bhp_general_valid_until ?? null,
        bhp_position_valid_until: form.bhp_position_valid_until ?? null,
        kadry_work_mode: form.kadry_work_mode ?? null
      };
      if (form.kind === 'user') {
        payload.telefon = form.phone;
      } else {
        payload.first_name = form.first_name || undefined;
        payload.last_name = form.last_name || undefined;
        payload.display_name = form.display_name;
        payload.email = form.email;
        payload.phone = form.phone;
        payload.pesel = (form as any).pesel ?? null;
        payload.is_active = form.is_active;
        payload.notes = form.kadry_notes;
      }
      const saved = await updateEmployee(form.kind, form.id, payload);
      setForm({ ...saved });
      onSaved();
    } catch (e: any) {
      console.error('[KadryPage] update failed', e);
      window.alert('Nie udało się zapisać: ' + (e?.response?.data?.message || e?.message || 'błąd'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddDoc = async () => {
    if (!docForm.title.trim() || !docForm.url.trim()) {
      window.alert('Podaj tytuł i URL dokumentu.');
      return;
    }
    try {
      const newDoc = await createEmployeeDocument(employee.kind, employee.id, {
        title: docForm.title.trim(),
        url: docForm.url.trim(),
        doc_type: docForm.doc_type.trim() || null,
        notes: docForm.notes.trim() || null
      });
      setDocs(prev => [newDoc, ...prev]);
      setDocForm({ title: '', url: '', doc_type: '', notes: '' });
    } catch (e: any) {
      window.alert('Nie udało się dodać dokumentu: ' + (e?.response?.data?.message || e?.message || 'błąd'));
    }
  };

  const handleDeleteDoc = async (doc: KadryDocument) => {
    const ok = window.confirm(`Usunąć dokument: ${doc.title}?`);
    if (!ok) return;
    try {
      await deleteEmployeeDocument(doc.id);
      setDocs(prev => prev.filter(d => d.id !== doc.id));
    } catch {}
  };

  const advanceRequests = leaves.filter(l => /zalicz/i.test(l.title || ''));
  const vacationRequests = leaves.filter(l => !/zalicz/i.test(l.title || ''));

  return (
    <Drawer anchor="right" open={Boolean(employee)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 680 } } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${BORDER_LIGHT}` }}>
        <Avatar src={form.avatar_url || undefined} sx={{ width: 48, height: 48, bgcolor: form.kind === 'user' ? PRIMARY_BLUE : TEXT_MUTED }}>
          {initialsFromName(form.full_name)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 18, fontWeight: 600, color: HEADER_TEXT, m: 0 }}>{form.full_name}</Typography>
          <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
            {form.kind === 'user' ? 'Użytkownik aplikacji' : 'Pracownik fizyczny'} · {form.position || 'brak stanowiska'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => onTabChange(v)}
        sx={{
          borderBottom: `1px solid ${BORDER_LIGHT}`,
          px: 2,
          minHeight: 40,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 14,
            fontWeight: 400,
            color: HEADER_TEXT,
            minHeight: 40
          },
          '& .Mui-selected': { fontWeight: 500 },
          '& .MuiTabs-indicator': { backgroundColor: PRIMARY_BLUE }
        }}
      >
        <Tab label="Dane" />
        <Tab label={`Urlopy (${vacationRequests.length})`} />
        <Tab label={`Zaliczki (${advanceRequests.length})`} />
        <Tab label={`Dokumenty (${docs.length})`} />
      </Tabs>

      <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
        {tab === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {form.kind === 'external' && (
              <>
                <TextField size="small" label="Imię" value={form.first_name || ''} onChange={(e) => set({ first_name: e.target.value })} fullWidth />
                <TextField size="small" label="Nazwisko" value={form.last_name || ''} onChange={(e) => set({ last_name: e.target.value })} fullWidth />
              </>
            )}
            <TextField size="small" label="Stanowisko" value={form.position || ''} onChange={(e) => set({ position: e.target.value })} fullWidth sx={{ gridColumn: { sm: '1 / span 2' } }} />
            <TextField size="small" label="Email" value={form.email || ''} onChange={(e) => set({ email: e.target.value })} fullWidth disabled={form.kind === 'user'} />
            <TextField size="small" label="Telefon" value={form.phone || ''} onChange={(e) => set({ phone: e.target.value })} fullWidth />
            {form.kind === 'external' && (
              <TextField size="small" label="PESEL" value={(form as any).pesel || ''} onChange={(e) => set({ ...(form as any), pesel: e.target.value } as any)} fullWidth />
            )}
            <TextField size="small" label="Data zatrudnienia" type="date" InputLabelProps={{ shrink: true }} value={form.hire_date?.slice(0, 10) || ''} onChange={(e) => set({ hire_date: e.target.value })} fullWidth />
            <TextField size="small" label="Data zwolnienia" type="date" InputLabelProps={{ shrink: true }} value={form.termination_date?.slice(0, 10) || ''} onChange={(e) => set({ termination_date: e.target.value })} fullWidth />

            <Divider sx={{ gridColumn: { sm: '1 / span 2' }, my: 1 }} />
            <Typography sx={{ gridColumn: { sm: '1 / span 2' }, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: HEADER_TEXT }}>Umowa i szkolenia BHP</Typography>
            <Typography variant="caption" sx={{ gridColumn: { sm: '1 / span 2' }, color: TEXT_MUTED, display: 'block', mt: -1 }}>
              W Polsce obowiązkowe jest szkolenie ogólne BHP (przed dopuszczeniem do pracy) oraz szkolenie stanowiskowe, dostosowane do stanowiska, zagrożeń i środków ochrony (Kodeks pracy). Okresy ważności badań i szkoleń ustala pracodawca zgodnie z przepisami i oceną ryzyka.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Typ umowy</InputLabel>
              <Select
                label="Typ umowy"
                value={form.contract_type || ''}
                onChange={(e: SelectChangeEvent) => set({ contract_type: e.target.value || null })}
              >
                <MenuItem value="">—</MenuItem>
                <MenuItem value="uop">UoP</MenuItem>
                <MenuItem value="b2b">B2B</MenuItem>
                <MenuItem value="uz">Umowa zlecenie</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Charakter umowy</InputLabel>
              <Select
                label="Charakter umowy"
                value={form.contract_is_fixed_term === true ? 'fixed' : form.contract_is_fixed_term === false ? 'indefinite' : ''}
                onChange={(e: SelectChangeEvent) => {
                  const v = e.target.value;
                  if (v === 'fixed') set({ contract_is_fixed_term: true });
                  else if (v === 'indefinite') set({ contract_is_fixed_term: false, contract_end_date: null });
                  else set({ contract_is_fixed_term: null, contract_end_date: null });
                }}
              >
                <MenuItem value="">—</MenuItem>
                <MenuItem value="fixed">Na czas określony</MenuItem>
                <MenuItem value="indefinite">Na czas nieokreślony</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Data zakończenia umowy (czas określony)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.contract_end_date?.slice(0, 10) || ''}
              onChange={(e) => set({ contract_end_date: e.target.value || null })}
              fullWidth
              disabled={form.contract_is_fixed_term !== true}
            />
            <FormControl fullWidth size="small" sx={{ gridColumn: { sm: '1 / span 2' } }}>
              <InputLabel>Miejsce pracy (zakładki na liście)</InputLabel>
              <Select
                label="Miejsce pracy (zakładki na liście)"
                value={form.kadry_work_mode || ''}
                onChange={(e: SelectChangeEvent) => set({ kadry_work_mode: e.target.value || null })}
              >
                <MenuItem value="">—</MenuItem>
                <MenuItem value="field">Teren i produkcja</MenuItem>
                <MenuItem value="office">Praca biurowa</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="BHP ogólne — ważne do" type="date" InputLabelProps={{ shrink: true }} value={form.bhp_general_valid_until?.slice(0, 10) || ''} onChange={(e) => set({ bhp_general_valid_until: e.target.value })} fullWidth />
            <TextField size="small" label="BHP stanowiskowe — ważne do" type="date" InputLabelProps={{ shrink: true }} value={form.bhp_position_valid_until?.slice(0, 10) || ''} onChange={(e) => set({ bhp_position_valid_until: e.target.value })} fullWidth />

            <Divider sx={{ gridColumn: { sm: '1 / span 2' }, my: 1 }} />
            <Typography sx={{ gridColumn: { sm: '1 / span 2' }, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: HEADER_TEXT }}>Urlopy</Typography>
            <TextField size="small" label="Przysługujący urlop (dni)" type="number" value={form.leave_days_entitlement ?? ''} onChange={(e) => set({ leave_days_entitlement: e.target.value === '' ? null : Number(e.target.value) })} fullWidth />
            <TextField
              size="small"
              label="Bilans ręczny — wykorzystane (dni)"
              type="number"
              value={form.leave_days_used ?? ''}
              onChange={(e) => set({ leave_days_used: e.target.value === '' ? null : Number(e.target.value) })}
              fullWidth
              helperText={
                form.kind === 'user' && form.leave_annual_approved_from_requests != null
                  ? `+ ${form.leave_annual_approved_from_requests} dni z wniosków (bieżący rok, urlop wyp./na żądanie — tylko po akceptacji HR). Łącznie: ${form.leave_days_used_total != null ? form.leave_days_used_total : '—'}.`
                  : form.kind === 'user'
                    ? 'Opcjonalny bilans przed pełnym wdrożeniem; do sumy doliczane są zatwierdzone wnioski urlopowe.'
                    : undefined
              }
            />
            <TextField
              size="small"
              label="Pozostało (wyliczane)"
              value={
                form.leave_days_entitlement != null
                  ? (() => {
                      const manual = form.leave_days_used != null ? Number(form.leave_days_used) : 0;
                      const fromReq = form.leave_annual_approved_from_requests != null ? Number(form.leave_annual_approved_from_requests) : 0;
                      if (form.kind === 'user' && (form.leave_days_used != null || fromReq)) {
                        return Math.round((Number(form.leave_days_entitlement) - manual - fromReq) * 10) / 10;
                      }
                      if (form.leave_days_used != null) {
                        return Math.round((Number(form.leave_days_entitlement) - Number(form.leave_days_used)) * 10) / 10;
                      }
                      return '—';
                    })()
                  : '—'
              }
              fullWidth
              disabled
              sx={{ gridColumn: { sm: '1 / span 2' } }}
            />

            <Divider sx={{ gridColumn: { sm: '1 / span 2' }, my: 1 }} />
            <Typography sx={{ gridColumn: { sm: '1 / span 2' }, fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: HEADER_TEXT }}>Badania lekarskie</Typography>
            <TextField size="small" label="Ostatnie badania" type="date" InputLabelProps={{ shrink: true }} value={form.medical_exam_last_date?.slice(0, 10) || ''} onChange={(e) => set({ medical_exam_last_date: e.target.value })} fullWidth />
            <TextField size="small" label="Ważne do" type="date" InputLabelProps={{ shrink: true }} value={form.medical_exam_valid_until?.slice(0, 10) || ''} onChange={(e) => set({ medical_exam_valid_until: e.target.value })} fullWidth />
            <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
              <Chip size="small" label={medicalStatus(form.medical_exam_valid_until).label} color={medicalStatus(form.medical_exam_valid_until).color as any} variant="outlined" />
            </Box>

            <Divider sx={{ gridColumn: { sm: '1 / span 2' }, my: 1 }} />
            <TextField size="small" label="Notatki kadrowe" value={form.kadry_notes || ''} onChange={(e) => set({ kadry_notes: e.target.value })} multiline minRows={3} fullWidth sx={{ gridColumn: { sm: '1 / span 2' } }} />

            {form.google_drive_user_folder_link && (
              <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                <MuiLink href={form.google_drive_user_folder_link} target="_blank" rel="noreferrer">
                  Otwórz folder pracownika na Google Drive <OpenInNew fontSize="inherit" />
                </MuiLink>
              </Box>
            )}
          </Box>
        )}

        {tab === 1 && (<LeaveList title="Urlopy" rows={vacationRequests} kind={employee.kind} />)}
        {tab === 2 && (<LeaveList title="Zaliczki" rows={advanceRequests} kind={employee.kind} />)}

        {tab === 3 && (
          <Box>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: HEADER_TEXT, mb: 1 }}>Dodaj link do dokumentu</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
              <TextField size="small" label="Tytuł *" value={docForm.title} onChange={(e) => setDocForm(f => ({ ...f, title: e.target.value }))} fullWidth />
              <TextField size="small" label="Typ (np. umowa, bhp, badania)" value={docForm.doc_type} onChange={(e) => setDocForm(f => ({ ...f, doc_type: e.target.value }))} fullWidth />
              <TextField size="small" label="URL *" value={docForm.url} onChange={(e) => setDocForm(f => ({ ...f, url: e.target.value }))} fullWidth sx={{ gridColumn: { sm: '1 / span 2' } }} />
              <TextField size="small" label="Notatki" value={docForm.notes} onChange={(e) => setDocForm(f => ({ ...f, notes: e.target.value }))} fullWidth sx={{ gridColumn: { sm: '1 / span 2' } }} />
              <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                <Button
                  variant="contained"
                  onClick={handleAddDoc}
                  startIcon={<AddIcon />}
                  sx={{ textTransform: 'none', backgroundColor: PRIMARY_BLUE, '&:hover': { backgroundColor: PRIMARY_BLUE_HOVER } }}
                >
                  Dodaj dokument
                </Button>
              </Box>
            </Box>

            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: HEADER_TEXT, mt: 2, mb: 1 }}>Lista dokumentów</Typography>
            {docs.length === 0 ? (
              <Typography variant="body2" sx={{ color: TEXT_MUTED }}>Brak dokumentów</Typography>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${BORDER_LIGHT}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tytuł</TableCell>
                      <TableCell sx={{ width: 120 }}>Typ</TableCell>
                      <TableCell sx={{ width: 140 }}>Dodano</TableCell>
                      <TableCell align="right" sx={{ width: 100 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {docs.map(d => (
                      <TableRow key={d.id}>
                        <TableCell>
                          <MuiLink href={d.url} target="_blank" rel="noreferrer">
                            {d.title} <OpenInNew fontSize="inherit" />
                          </MuiLink>
                          {d.notes && (<Typography variant="caption" sx={{ display: 'block', color: TEXT_MUTED }}>{d.notes}</Typography>)}
                        </TableCell>
                        <TableCell>{d.doc_type || '—'}</TableCell>
                        <TableCell>{fmtDate(d.created_at)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Usuń">
                            <IconButton size="small" onClick={() => handleDeleteDoc(d)} sx={{ color: '#B91C1C' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: `1px solid ${BORDER_LIGHT}`, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: TEXT_DARK }}>Zamknij</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none', backgroundColor: PRIMARY_BLUE, '&:hover': { backgroundColor: PRIMARY_BLUE_HOVER } }}
        >
          {saving ? 'Zapisywanie…' : 'Zapisz zmiany'}
        </Button>
      </Box>
    </Drawer>
  );
};

const LeaveList: React.FC<{ title: string; rows: KadryLeaveRequest[]; kind: EmployeeKind }> = ({ title, rows, kind }) => {
  if (kind === 'external') {
    return (
      <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
        Pracownicy fizyczni bez konta w aplikacji nie składają wniosków przez aplikację. Rejestruj urlopy ręcznie w zakładce „Dane".
      </Typography>
    );
  }
  if (rows.length === 0) {
    return (<Typography variant="body2" sx={{ color: TEXT_MUTED }}>Brak wniosków typu „{title}".</Typography>);
  }
  return (
    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${BORDER_LIGHT}` }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nr</TableCell>
            <TableCell>Data</TableCell>
            <TableCell>Okres</TableCell>
            <TableCell>Dni</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>PDF</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.id}>
              <TableCell>{r.request_id}</TableCell>
              <TableCell>{fmtDate(r.request_date)}</TableCell>
              <TableCell>{fmtDate(r.date_from)} → {fmtDate(r.date_to)}</TableCell>
              <TableCell>{r.days}</TableCell>
              <TableCell>
                <Chip size="small" label={r.status} color={r.status === 'zgoda' ? 'success' : r.status === 'odrzucony' ? 'error' : 'info'} variant="outlined" />
              </TableCell>
              <TableCell>
                {r.generated_pdf_url ? (
                  <MuiLink href={r.generated_pdf_url} target="_blank" rel="noreferrer">PDF <OpenInNew fontSize="inherit" /></MuiLink>
                ) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default KadryPage;
